import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dependency, DependencyType, Task, TaskId } from '@bigint-project/core';
import {
  DEPENDENCY_TYPES,
  buildPredecessorCandidates,
  draftsFromDependencies,
  draftsToInput,
  isValidLagText,
  toggleDraft,
  updateDraft,
  type PredecessorDraft,
} from './predecessor-picker.js';

export interface PredecessorPickerProps {
  readonly taskId: TaskId;
  /** 完整任務清單(未過濾收合);WBS ref 與列號退路都以此為準。 */
  readonly tasks: readonly Task[];
  readonly dependencies: readonly Dependency[];
  /** 面板在表格容器內的定位(px)。 */
  readonly top: number;
  readonly left: number;
  /**
   * 送出勾選結果。字串與手動輸入的語法完全相同(例如 `3FS+2d,5SS`),
   * 由呼叫端走既有的 buildDependenciesFromInput 落地 —— 兩條路徑共用同一套驗證。
   */
  readonly onCommit: (input: string) => void;
  readonly onCancel: () => void;
  /** 落地失敗時由呼叫端回填的錯誤訊息(面板保持開啟讓使用者修正)。 */
  readonly error?: string | null;
}

/**
 * 前置任務下拉勾選面板。
 *
 * 這是「快速路徑(直接打字)」之外的第二條輸入方式,不取代文字輸入:
 * 熟 MS Project 語法的人繼續按 Enter 打 `3FS+2d`,不熟的人點 ▾ 從清單勾選。
 */
export function PredecessorPicker(props: PredecessorPickerProps): React.JSX.Element {
  const { taskId, tasks, dependencies, top, left, onCommit, onCancel, error } = props;

  const [drafts, setDrafts] = useState<readonly PredecessorDraft[]>(() =>
    draftsFromDependencies(taskId, dependencies, tasks),
  );
  const [filter, setFilter] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const candidates = useMemo(
    () => buildPredecessorCandidates(taskId, tasks, dependencies, drafts, filter),
    [dependencies, drafts, filter, taskId, tasks],
  );

  const draftByTaskId = useMemo(() => {
    const map = new Map<TaskId, PredecessorDraft>();
    for (const draft of drafts) map.set(draft.taskId, draft);
    return map;
  }, [drafts]);

  const hasInvalidLag = drafts.some((draft) => !isValidLagText(draft.lagText));

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) onCancel();
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel]);

  return (
    <div
      ref={panelRef}
      data-testid="predecessor-picker"
      role="dialog"
      aria-label="選擇前置任務"
      className="absolute z-50 flex w-[380px] flex-col rounded-md border border-line-strong bg-surface text-xs shadow-xl"
      style={{ top, left }}
      // 面板內的鍵盤操作(打字搜尋、Enter)不應被表格的格線導覽攔截。
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="border-b border-line px-2 py-1.5">
        <input
          type="text"
          autoFocus
          aria-label="搜尋任務"
          placeholder="搜尋 WBS / 名稱 / 列號"
          className="w-full rounded border border-line-strong bg-surface px-2 py-1 outline-none focus:border-focus"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </div>

      <div
        data-testid="predecessor-picker-list"
        className="max-h-64 overflow-auto py-1"
        role="listbox"
        aria-multiselectable
      >
        {candidates.length === 0 ? (
          <div className="px-3 py-2 text-fg-muted">沒有符合的任務</div>
        ) : null}
        {candidates.map((candidate) => {
          const draft = draftByTaskId.get(candidate.taskId);
          const label = `${candidate.ref} ${candidate.name}`;
          return (
            <div
              key={candidate.taskId}
              data-testid={`predecessor-option-${candidate.rowNumber}`}
              className="flex flex-wrap items-center gap-1 px-2 py-1 hover:bg-raised"
            >
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={label}
                  checked={candidate.selected}
                  onChange={() => setDrafts((current) => toggleDraft(current, candidate.taskId))}
                />
                <span className="w-12 shrink-0 text-right tabular-nums text-fg-muted">
                  {candidate.ref}
                </span>
                <span className="truncate text-fg">{candidate.name}</span>
              </label>

              {draft ? (
                <span className="flex shrink-0 items-center gap-1">
                  <select
                    aria-label={`${candidate.ref} 的依賴類型`}
                    className="rounded border border-line-strong bg-surface px-1 py-0.5"
                    value={draft.type}
                    onChange={(event) =>
                      setDrafts((current) =>
                        updateDraft(current, candidate.taskId, {
                          type: event.target.value as DependencyType,
                        }),
                      )
                    }
                  >
                    {DEPENDENCY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    aria-label={`${candidate.ref} 的延隔時間`}
                    placeholder="+2d"
                    className={[
                      'w-16 rounded border bg-surface px-1 py-0.5',
                      isValidLagText(draft.lagText)
                        ? 'border-line-strong'
                        : 'border-danger',
                    ].join(' ')}
                    value={draft.lagText}
                    onChange={(event) =>
                      setDrafts((current) =>
                        updateDraft(current, candidate.taskId, { lagText: event.target.value }),
                      )
                    }
                  />
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {error ? (
        <div
          role="alert"
          data-testid="predecessor-picker-error"
          className="border-t border-danger/40 bg-danger-soft px-2 py-1 text-danger"
        >
          {error}
        </div>
      ) : null}

      <div className="flex items-center justify-between border-t border-line px-2 py-1.5">
        <span className="text-fg-muted">已選 {drafts.length} 筆</span>
        <span className="flex gap-2">
          <button
            type="button"
            className="rounded px-2 py-1 text-fg-muted hover:bg-raised"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            disabled={hasInvalidLag}
            className="rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onCommit(draftsToInput(drafts, tasks))}
          >
            確定
          </button>
        </span>
      </div>
    </div>
  );
}
